import Config from 'react-native-config';
import { HealthPlan } from 'store/amwell/types';
import { JsonObj } from '@mhp/general-interface';
import { Color } from 'modules/styles/colors';

import { SvgSet } from 'components/UI/Svg';
import { exitModalText, exitEVisit } from './exitConfirmModal';

export const mimeTypes = [
  '.pdf,',
  ' .jpeg,',
  ' .png,',
  ' .bmp,',
  ' .gif,',
  ' .tif,',
  ' .tiff,',
  ' .wav,',
  ' .aiff,',
  ' .mpeg,',
  ' .ogg,',
  ' .mp4,',
  ' .mp3,',
  ' .m4a,'
];

// * * * * * * * * * * * * * * * * * * * * * //
// *  CC LANDING SECTION & MODAL CONSTANTS * //
// * * * * * * * * * * * * * * * * * * * * * //

export const HIDE_IMAGE_BREAKPOINT = 1200;

// ///// MODALS ///// //
export const CONDITIONS_WE_TREAT = 'Conditions we treat';

const urgentCareLineColor = Color.green;
const behavioralHealthLineColor = Color.purple;
const primaryCareLineColor = Color.secondary;
const eVisitLineColor = Color.lightBlue;

export const urgentCareModalName = 'urgent-care-modal';
export const urgentCarePhysicalTherapyModalName = 'urgent-care-physical-therapy-modal';
export const behavioralHealthModalName = 'behavioral-health-modal';
export const primaryCareModalName = 'virtual-primary-care-modal';
export const eVisitModalName = 'e-visit-modal';

export const urgentCareModal = {
  name: urgentCareModalName,
  color: urgentCareLineColor,
  headerTitle: 'Urgent Care',
  title: 'Commonly Treated Conditions',
  description: `Connect Care Urgent Care is able to treat most everyday illnesses including those described below. However, if during your visit we feel you need to be seen in person, we'll refer you to a provider and we won't charge you for your Connect Care visit.`,
  treatedConditions: [
    { id: 'uctc01', sentence: 'Allergies' },
    { id: 'uctc02', sentence: 'Colds' },
    { id: 'uctc03', sentence: 'Cough' },
    { id: 'uctc04', sentence: 'Eye infections' },
    { id: 'uctc05', sentence: 'Flu' },
    {
      id: 'uctc06',
      sentence: 'Joint pain or strains'
    },
    { id: 'uctc07', sentence: 'Lower back pain' },
    { id: 'uctc08', sentence: 'Minor skin problems' },
    {
      id: 'uctc09',
      sentence: 'Painful urination (women only)',
      boldWords: [3, 4]
    },
    { id: 'uctc10', sentence: 'Sore throat' },
    { id: 'uctc11', sentence: 'Stuffy and runny nose' }
  ],
  note: {
    sentence: 'Please note: Intermountain providers cannot treat ear infections on Connect Care.',
    boldWords: [7, 8]
  }
};

export const urgentCarePhysicalTherapyModal = {
  name: urgentCarePhysicalTherapyModalName,
  color: urgentCareLineColor,
  headerTitle: 'Connect Care Physical Therapy',
  title: 'Commonly Treated Conditions',
  description: ``,
  treatedConditions: [
    { id: 'uctc01', sentence: 'Back pain' },
    { id: 'uctc02', sentence: 'Neck pain' },
    { id: 'uctc03', sentence: 'Whiplash' },
    { id: 'uctc04', sentence: 'Ankle sprain' },
    { id: 'uctc05', sentence: 'Muscle strain/soreness' },
    {
      id: 'uctc06',
      sentence: 'Joint pain (hip, knee, shoulder, elbow)'
    },
    { id: 'uctc07', sentence: 'Sports injury' },
    { id: 'uctc08', sentence: 'Heel pain' }
  ]
};

export const behavioralHealthModal = {
  name: behavioralHealthModalName,
  color: behavioralHealthLineColor,
  headerTitle: 'Behavioral Health',
  title: 'Commonly Treated Conditions',
  description: `Connect Care Behavioral Health is making treatment for your mild-to-moderate mental health conditions including medication management more accessible with extended hours and the convenience of meeting wherever you are through video technology.`,
  treatedConditions: [
    { id: 'bhtc00', sentence: 'Anger or Mood Swings' },
    { id: 'bhtc01', sentence: 'Anxiety' },
    { id: 'bhtc02', sentence: 'Attention Deficit' },
    { id: 'bhtc03', sentence: 'Bipolar' },
    { id: 'bhtc04', sentence: 'Depression' },
    { id: 'bhtc05', sentence: 'Depression during Pregnancy' },
    { id: 'bhtc06', sentence: 'Insomnia' },
    { id: 'bhtc07', sentence: 'Panic Attacks' },
    { id: 'bhtc08', sentence: 'Post-Partum Depression' },
    { id: 'bhtc09', sentence: 'PTSD' },
    { id: 'bhtc10', sentence: 'Stress' },
    { id: 'bhtc11', sentence: 'Substance Misuse/Abuse' }
  ]
};

export const virtualPrimaryCareModal = {
  name: primaryCareModalName,
  color: primaryCareLineColor,
  headerTitle: 'Primary Care',
  title: 'Commonly Treated Conditions',
  description:
    'Virtual Primary Care is meant to provide full spectrum primary care services for patients. Most conditions can be cared for virtually, but there are circumstances that require in-person visits. If you need in-person care, or to connect with a specialty provider, we will help you access the care you need. Some of the most common conditions we treat include:',
  secondaryConditions: [
    {
      sentence:
        'Chronic Conditions: asthma, diabetes, heart and lung disease, mental health, etc. ',
      boldWords: [1, 2],
      id: 'sc01'
    },
    {
      sentence:
        'Preventive Care: cancer screenings, vaccines, labs, weight management, referrals to specialty services such as nutritionists and care coordinators, etc. Please check with your insurance payer about coverage of preventative visits done via telehealth.',
      boldWords: [1, 2],
      id: 'sc02'
    },
    {
      sentence:
        'Referrals to medical specialists: cardiology, endocrinology, neurology, and many others.',
      boldWords: [1, 2, 3, 4],
      id: 'sc03'
    },
    {
      sentence:
        'Acute care visits: (Please note: Intermountain providers cannot treat ear infections via video. We are also unable to treat croup with steroids via video.)',
      boldWords: [1, 2, 3, 10, 11],
      id: 'sc04'
    }
  ],
  treatedConditions: [
    { id: 'pctc01', sentence: 'Allergies' },
    { id: 'pctc02', sentence: 'Colds' },
    { id: 'pctc03', sentence: 'Cough' },
    { id: 'pctc04', sentence: 'Eye infections' },
    { id: 'pctc05', sentence: 'Flu' },
    {
      id: 'pctc06',
      sentence: 'Joint pain or strains'
    },
    { id: 'pctc07', sentence: 'Lower back pain' },
    {
      id: 'pctc08',
      sentence: 'Painful urination (women only)',
      boldWords: [3, 4]
    },
    { id: 'pctc09', sentence: 'Sore throat' },
    { id: 'pctc10', sentence: 'Stuffy and runny nose' }
  ]
};

export const eVisitModal = {
  name: eVisitModalName,
  color: eVisitLineColor,
  headerTitle: 'E-Visit',
  title: 'Commonly Treated Conditions',
  description: `E-Visits can be used to treat low acute conditions described below. However, if during your visit we feel you need to be seen in person, we'll provide you with other care options and will not charge you for using this service.`,
  treatedConditions: [
    { id: 'evtc00', sentence: 'Head Cold / Cough' },
    { id: 'evtc01', sentence: 'Sinus infection' },
    { id: 'evtc02', sentence: 'Seasonal allergies' },
    { id: 'evtc03', sentence: 'Sore throat' },
    { id: 'evtc04', sentence: 'Flu' },
    { id: 'evtc05', sentence: 'Diarrhea' },
    { id: 'evtc06', sentence: 'Nausea' },
    { id: 'evtc07', sentence: 'UTI' },
    { id: 'evtc08', sentence: 'Vomiting' },
    { id: 'evtc09', sentence: 'Pink eye' },
    { id: 'evtc10', sentence: 'Stye' },
    { id: 'evtc11', sentence: 'Vaginal yeast infections' },
    { id: 'evtc12', sentence: 'Cold sore' }
  ]
};

// ////////SECTIONS //////// //

export const urgentCareSectionTitle = 'Connect Care Urgent Care';
export const urgentPhysicalCareSectionTitle = 'Connect Care Physical Therapy';
export const behavioralHealthSectionTitle = 'Connect Care Behavioral Health';
export const virtualPrimaryCareSectionTitle = 'Virtual Primary Care';
export const eVisitSectionTitle = 'E-Visit';

// For web an array of strings (for images) are sent to this function
// Mobile does not need images
const connectCareLandingSections = (dependsOnSH = true, showEVisit = true, images: string[]) => [
  {
    showCard: true,
    key: 'urgent-care',
    imgSrc: images[0],
    color: urgentCareLineColor,
    title: urgentCareSectionTitle,
    subTitle: 'Next available video visit',
    description: `Connect Care is a great option to access convenient, high-quality urgent care (including physical therapy) whenever and wherever you need it. Most insurance carriers now cover Connect Care, which means your visit may be free. $79 is the most you'll pay for your visit.`,
    linkLabel: CONDITIONS_WE_TREAT,
    buttonLabel: 'Start Connect Care',
    redirectTo: {
      web: '/u/get-care-now/connect-care/form/location',
      mobile: 'ConnectCareTestMyDevice'
    },
    modal: urgentCareModal,
    accessibilityLabel: 'Tap to start Connect Care'
  },
  {
    showCard: true,
    key: 'physical-therapy-urgent-care',
    imgSrc: images[4],
    color: urgentCareLineColor,
    title: urgentPhysicalCareSectionTitle,
    subTitle: 'Next available video visit',
    description: `Connect Care Physical Therapy is a great option to receive quick care for aches, pains, and muscle strains by visiting with a board-certified physical therapist in minutes. Most insurance carriers now cover Connect Care Physical Therapy, which means your visit may be free. $79 is the most you'll pay for your visit.`,
    linkLabel: 'Common injuries we treat',
    buttonLabel: 'Start Connect Care',
    redirectTo: {
      web: '/u/get-care-now/connect-care/form/location',
      mobile: 'ConnectCareTestMyDevice'
    },
    modal: urgentCarePhysicalTherapyModal,
    accessibilityLabel: 'Tap to start Connect Care'
  },
  {
    showCard: true,
    key: 'behavioral-health',
    imgSrc: images[1],
    color: behavioralHealthLineColor,
    title: behavioralHealthSectionTitle,
    subTitle: 'Scheduled video appointment',
    description: `Psychiatric Care is now available from anywhere in Utah or Idaho using your smart phone, tablet, or computer. Receive the same quality care for your mild-to-moderate conditions for anxiety, depression, addiction and more.`,
    linkLabel: CONDITIONS_WE_TREAT,
    buttonLabel: 'Schedule Appointment',
    redirectTo: '/u/get-care-now/booking/patient-select',
    modal: behavioralHealthModal,
    accessibilityLabel: 'Tap to schedule behavioral health visit'
  },
  {
    showCard: Config.VIRTUAL_PRIMARY_CARE === 'enabled' && dependsOnSH,
    key: 'primary-care',
    imgSrc: images[2],
    color: primaryCareLineColor,
    title: virtualPrimaryCareSectionTitle,
    subTitle: 'Scheduled video appointment',
    description: `Virtual Primary Care is a virtual video experience for patients wanting a more convenient way to engage in primary care.`,
    linkLabel: CONDITIONS_WE_TREAT,
    buttonLabel: 'Schedule Appointment',
    redirectTo: '',
    modal: virtualPrimaryCareModal,
    accessibilityLabel: 'Tap to schedule primary care visit'
  },
  {
    showCard: Config.EVISIT_FRONTDOOR === 'enabled' && showEVisit,
    key: 'e-visit',
    imgSrc: images[3],
    color: eVisitLineColor,
    title: eVisitSectionTitle,
    subTitle: 'Send and receive a secure message',
    description: `An E-Visit is an easy way to get care for low-risk conditions without ever stepping into a clinic. Answer a few questions and carry on with your day. An urgent care provider will review your situation and create a personalized treatment plan. E-Visits are $35 or less with some insurance plans.`,
    linkLabel: CONDITIONS_WE_TREAT,
    buttonLabel: 'Start an E-Visit',
    redirectTo: '',
    modal: eVisitModal,
    accessibilityLabel: 'Tap to start E visit'
  }
];

// Deep link values
const CCBH_ROUTE = 'CCBH';
// TO DO, re-check when start working on story about deeplinking and Virtual Primary Care
const VPC_ROUTE = 'VPC';

const CCEV_ROUTE = 'CCEV';

export const reArrangeSections = (
  deepLinkValue: string | null,
  sectionImages: string[],
  dependsOnSH = true,
  showEVisit = true
) => {
  const auxSections = connectCareLandingSections(dependsOnSH, showEVisit, sectionImages);
  const [urgentCare, behavioralHealth, primaryCare, eVisit] = auxSections;
  switch (deepLinkValue) {
    case CCBH_ROUTE:
      return [behavioralHealth, urgentCare, primaryCare, eVisit];
    // TO DO, re-check when start working on story about deeplinking and Virtual Primary Care
    case VPC_ROUTE:
      return [primaryCare, urgentCare, behavioralHealth, eVisit];
    case CCEV_ROUTE:
      return [eVisit, urgentCare, behavioralHealth, primaryCare];
    default:
      return auxSections;
  }
};

export enum ConnectCareAccountLinkType {
  MANUAL = 'Manual',
  AUTOMATIC = 'Automatic',
  NEW = 'New'
}

export const exitConnectCare = {
  ...exitModalText('Connect Care')
};

// * * * * * * * * * * * * * * * *//
// *  TEST MY DEVICE CONSTANTS  * //
// * * * * * * * * * * * * * * * *//

export const doneStatus = 'done';
export const activeStatus = 'active';
export const defaultStatus = 'default';
export const skipStatus = 'skip';

export const TestMyDeviceHeaderTitle = 'Test My Device';

export type Status =
  | typeof doneStatus
  | typeof activeStatus
  | typeof defaultStatus
  | typeof skipStatus;

type label = 'Start' | 'Camera' | 'Mic' | 'Speaker' | 'Internet' | 'Summary';

interface ScreenContentSingleOption {
  labelId: label;
  title: string;
  titleNegative?: string;
  content?: string;
  buttonLabel: string;
  noOption?: never;
  yesOption?: never;
  status: Status;
  onCompleteStep?: Function;
}
interface ScreenContentBothOptions {
  labelId: label;
  title: string;
  titleNegative?: string;
  content?: string;
  buttonLabel?: never;
  noOption: string;
  yesOption: string;
  status: Status;
  onCompleteStep?: Function;
}

export type ScreenContent = ScreenContentSingleOption | ScreenContentBothOptions;

export interface InfoStep {
  id: number;
  title: string;
  iconLabel: label;
  iconStatus: Status;
  iconName: string;
  iconSet: SvgSet;
  screenContent: ScreenContent;
}

interface TestMyDeviceProps {
  steps: InfoStep[];
}

export const TestMyDevice: TestMyDeviceProps = {
  steps: [
    {
      id: 0,
      iconLabel: 'Start',
      iconName: 'pulse',
      iconStatus: 'active',
      iconSet: 'community',
      screenContent: {
        labelId: 'Start',
        buttonLabel: 'Start Test',
        content: `On the following screens we'll check your camera, microphone, speakers and internet so you are ready for your visit.`,
        title: `Let's Get Started`
      },
      title: 'Get Started'
    },
    {
      id: 1,
      iconLabel: 'Camera',
      iconName: 'videocam',
      iconStatus: 'default',
      iconSet: 'ionic',
      screenContent: {
        labelId: 'Camera',
        content: '',
        title: 'Is your video area above?',
        noOption: 'No',
        yesOption: 'Yes'
      },
      title: 'Camera'
    },
    {
      id: 2,
      iconLabel: 'Mic',
      iconName: 'mic',
      iconStatus: 'default',
      iconSet: 'material',
      screenContent: {
        labelId: 'Mic',
        title: 'Speak or make a noise. Did the green bars move?',
        noOption: 'No',
        yesOption: 'Yes'
      },
      title: 'Microphone'
    },
    {
      id: 3,
      iconLabel: 'Speaker',
      iconStatus: 'default',
      iconName: 'volume-high',
      iconSet: 'ionic',
      screenContent: {
        labelId: 'Speaker',
        title: 'Click the play button. Did the sound play?',
        noOption: 'No',
        yesOption: 'Yes'
      },
      title: 'Speaker'
    },
    {
      id: 4,
      iconLabel: 'Internet',
      iconStatus: 'default',
      iconName: 'speedometer',
      iconSet: 'ionic',
      screenContent: {
        labelId: 'Internet',
        title:
          'Press the GO button to test if your internet connection is strong enough for a visit.',
        noOption: 'Skip Speed Test'
      },
      title: 'Internet'
    },
    {
      id: 5,
      iconLabel: 'Summary',
      iconStatus: 'default',
      iconName: 'list-outline',
      iconSet: 'ionic',
      screenContent: {
        labelId: 'Summary',
        title: 'Your device is ready for online visits',
        titleNegative: 'Your device is not ready for online visits',
        content: 'Please try the test again or call Support at (844) 624-4866 for assistance.',
        buttonLabel: `Let's Go`
      },
      title: 'Summary'
    }
  ]
};

export const resolveSvgColorStatus = (iconStatus: Status) => {
  switch (iconStatus) {
    case 'active':
      return Color.blueHue3;
    case 'done':
      return Color.green;
    case 'skip':
      return Color.yellow;
    default:
      return Color.gray;
  }
};

export const healthPlanNotListed: HealthPlan = {
  id: { encryptedId: 'notListedItem', persistentId: 'notListedItem' },
  name: 'Insurance Not Listed',
  qualifier: '',
  usesSuffix: false,
  enrollmentEnabled: false,
  payerInfo: {
    suppressCharge: true
  },
  links: [],
  feedControlled: false
};

export const eVisitConfirmDialog = {
  ...exitEVisit('E-Visit')
};

export const providerColorMap: JsonObj = {
  'success.main': Color.darkGreenShade,
  'warning.main': Color.alertError,
  'error.main': Color.darkGrayShade
} as const;
